import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CoursesSection() {
  const { t } = useTranslation();
  return (
    <section className="relative py-24 md:py-32 bg-background-100">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        {/* Section Header */}
        <div className="text-center mb-16">
          {String(t('home.courses.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 tracking-[0.06em] mb-4">
            {t('home.courses.title')}
          </h2>
          <div className="gold-line mx-auto mb-4"></div>
          <p className="text-[16px] text-foreground-600 tracking-[0.04em] max-w-xl mx-auto leading-relaxed">
            {t('home.courses.schedule')}<span className="text-accent-600 font-medium">{t('home.courses.scheduleDetail1')}</span>{t('home.courses.scheduleConnector1')}
            <span className="text-accent-600 font-medium">{t('home.courses.scheduleDetail2')}</span>{t('home.courses.scheduleConnector2')}
            <span className="text-accent-600 font-medium">{t('home.courses.scheduleDetail3')}</span>{t('home.courses.scheduleDetail3Sub')}
          </p>
        </div>

        {/* Course Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Essential Course Card */}
          <article className="card-glow bg-background-50 border border-background-300/60 group cursor-pointer">
            <div className="relative h-96 overflow-hidden">
              <img
                src="/images/generated/course-essential-img.jpg"
                alt={t('home.courses.essential.imgAlt')}
                title={t('home.courses.essential.imgTitle')}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-accent-500/90 text-primary-900 text-[12px] tracking-[0.1em] font-medium">
                  {t('home.courses.essential.tag')}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-3">
                {t('home.courses.essential.title')}
              </h3>
              {String(t('home.courses.essential.desc')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 leading-relaxed tracking-[0.03em] mb-5 min-h-[48px]">{__s}</p>))}
              <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-background-200">
                <div>
                  {String(t('home.courses.periodLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.essential.period')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.hoursLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.essential.hours')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.daysLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.essential.days')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.priceLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  <p className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">
                    {t('home.courses.essential.price')}<span className="text-[12px] text-foreground-500">{t('home.courses.taxNote')}</span>
                  </p>
                </div>
              </div>
              {String(t('home.courses.essential.target')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-600 tracking-[0.04em] mb-3">{__s}</p>))}
              {String(t('home.courses.essential.cert')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-700 tracking-[0.04em] mb-4 leading-relaxed">{__s}</p>))}
              <Link
                to="/courses/essential"
                className="inline-flex items-center gap-2 text-[14px] text-foreground-700 hover:text-accent-600 tracking-[0.08em] transition-colors duration-300 group/link"
              >
                <span>{t('home.courses.viewDetail')}</span>
                <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover/link:translate-x-1"></i>
              </Link>
            </div>
          </article>

          {/* Advanced Course Card */}
          <article className="card-glow bg-background-50 border border-background-300/60 group cursor-pointer">
            <div className="relative h-96 overflow-hidden">
              <img
                src="/images/generated/course-advanced-img.jpg"
                alt={t('home.courses.advanced.imgAlt')}
                title={t('home.courses.advanced.imgTitle')}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-accent-500/90 text-primary-900 text-[12px] tracking-[0.1em] font-medium">
                  {t('home.courses.advanced.tag')}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-3">
                {t('home.courses.advanced.title')}
              </h3>
              {String(t('home.courses.advanced.desc')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 leading-relaxed tracking-[0.03em] mb-5 min-h-[48px]">{__s}</p>))}
              <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-background-200">
                <div>
                  {String(t('home.courses.periodLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.advanced.period')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.hoursLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.advanced.hours')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.daysLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.advanced.days')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.priceLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  <p className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">
                    {t('home.courses.advanced.price')}<span className="text-[12px] text-foreground-500">{t('home.courses.taxNote')}</span>
                  </p>
                </div>
              </div>
              {String(t('home.courses.advanced.target')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-600 tracking-[0.04em] mb-3">{__s}</p>))}
              {String(t('home.courses.advanced.cert')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-700 tracking-[0.04em] mb-4 leading-relaxed">{__s}</p>))}
              <Link
                to="/courses/advanced"
                className="inline-flex items-center gap-2 text-[14px] text-foreground-700 hover:text-accent-600 tracking-[0.08em] transition-colors duration-300 group/link"
              >
                <span>{t('home.courses.viewDetail')}</span>
                <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover/link:translate-x-1"></i>
              </Link>
            </div>
          </article>

          {/* Master Course Card */}
          <article className="card-glow bg-background-50 border border-background-300/60 group cursor-pointer">
            <div className="relative h-96 overflow-hidden">
              <img
                src="/images/generated/course-master-img.jpg"
                alt={t('home.courses.master.imgAlt')}
                title={t('home.courses.master.imgTitle')}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-accent-500/90 text-primary-900 text-[12px] tracking-[0.1em] font-medium">
                  {t('home.courses.master.tag')}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-3">
                {t('home.courses.master.title')}
              </h3>
              {String(t('home.courses.master.desc')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 leading-relaxed tracking-[0.03em] mb-5 min-h-[48px]">{__s}</p>))}
              <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-background-200">
                <div>
                  {String(t('home.courses.periodLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.master.period')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.hoursLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.master.hours')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.daysLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  {String(t('home.courses.master.days')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">{__s}</p>))}
                </div>
                <div>
                  {String(t('home.courses.priceLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.06em] mb-1">{__s}</p>))}
                  <p className="text-[15px] text-foreground-800 font-medium tracking-[0.04em]">
                    {t('home.courses.master.price')}<span className="text-[12px] text-foreground-500">{t('home.courses.taxNote')}</span>
                  </p>
                </div>
              </div>
              {String(t('home.courses.master.target')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-600 tracking-[0.04em] mb-3">{__s}</p>))}
              {String(t('home.courses.master.cert')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-700 tracking-[0.04em] mb-4 leading-relaxed">{__s}</p>))}
              <Link
                to="/courses/master"
                className="inline-flex items-center gap-2 text-[14px] text-foreground-700 hover:text-accent-600 tracking-[0.08em] transition-colors duration-300 group/link"
              >
                <span>{t('home.courses.viewDetail')}</span>
                <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover/link:translate-x-1"></i>
              </Link>
            </div>
          </article>
        </div>

        {/* Schedule Link */}
        <div className="text-center">
          <Link
            to="/courses#schedule"
            className="inline-flex items-center gap-2 text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.08em] transition-colors duration-300 group"
          >
            <i className="ri-calendar-line"></i>
            <span>{t('home.courses.scheduleLink')}</span>
            <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}